import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { MoreVertical, MapPinPlus, Merge, Unlink, Split } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { plain } from "@/components/dock/DockSheet";
import { CountryPicker } from "@/components/travel/CountryPicker";
import { Avatar } from "@/components/friends/Avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);

const AddPlace = ({ p, names, open, onOpenChange }) => {
  const qc = useQueryClient();
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const save = async () => {
    try {
      const r = await api.addPersonPlace({ pid: p.key, country, city });
      toast.success(`${p.name} also saved under ${city ? `${city}, ` : ""}${r.country}`);
      setCountry(""); setCity(""); onOpenChange(false);
      qc.invalidateQueries({ queryKey: ["state"] });
    } catch (e) { err(e, "Couldn't add the place"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="person-add-place-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Add a place for {p.name}</DialogTitle>
          <DialogDescription>They'll show under every country and city you add.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <CountryPicker names={names} exclude={[]} onPick={setCountry} label={country || "Pick country"} testId="person-place-country"
            className={country ? "bg-amber-400/15 text-amber-200 hover:bg-amber-400/25 border border-amber-400/40" : "bg-white/10 text-slate-100 hover:bg-white/15"} />
          <Input placeholder="City (optional)" value={city} onChange={(e) => setCity(e.target.value)} className="flex-1" data-testid="person-place-city" />
        </div>
        <DialogFooter><Button onClick={save} disabled={!country} className="bg-emerald-400 text-[#04120c] hover:bg-emerald-300" data-testid="person-place-save">Add place</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MergeWith = ({ p, people, friends, open, onOpenChange }) => {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const t = plain(q.trim());
  const linked = new Set(people.map((x) => x.friendId).filter(Boolean));
  const options = [
    ...friends.filter((f) => !linked.has(f.friend_id)).map((f) => ({ id: `f-${f.friend_id}`, name: f.name, sub: "Friend on the bucketlist", f, body: { pid: p.key, friend_id: f.friend_id } })),
    ...people.filter((x) => x.key !== p.key).map((x) => ({ id: `c-${x.key}`, name: x.name, sub: `Saved contact · ${x.places.join(" · ")}`, body: { pid: x.key, into_pid: p.key } })),
  ].filter((o) => !t || plain(o.name).includes(t)).sort((a, b) => a.name.localeCompare(b.name));
  const pick = async (o) => {
    try {
      await api.mergePerson(o.body);
      toast.success(o.f ? `${p.name} is now linked to ${o.f.name}'s profile` : `${o.name} merged into ${p.name}`);
      onOpenChange(false); setQ("");
      qc.invalidateQueries({ queryKey: ["state"] });
    } catch (e) { err(e, "Couldn't merge"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setQ(""); onOpenChange(o); }}>
      <DialogContent className="max-w-md max-h-[85vh] flex flex-col" data-testid="person-merge-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Merge {p.name} with…</DialogTitle>
          <DialogDescription>Combines everything in your own list, keeping the name {p.name}. Nothing changes on anyone else's account.</DialogDescription>
        </DialogHeader>
        <Input placeholder="Search friends and contacts…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="person-merge-search" />
        <ul className="flex-1 overflow-y-auto -mx-1" data-testid="person-merge-options">
          {options.map((o) => (
            <li key={o.id}>
              <button onClick={() => pick(o)} className="w-full flex items-center gap-3 px-1 py-2.5 border-b border-white/5 text-left hover:bg-white/5 rounded transition-colors" data-testid={`person-merge-option-${o.id}`}>
                {o.f ? <Avatar f={o.f} size="w-8 h-8" /> : <span className="w-8 h-8 rounded-full bg-amber-400/10 text-amber-200 grid place-items-center text-xs font-semibold shrink-0">{o.name[0]?.toUpperCase()}</span>}
                <span className="min-w-0"><span className="block text-sm truncate">{o.name}</span><span className="block text-xs text-slate-500 truncate">{o.sub}</span></span>
              </button>
            </li>
          ))}
          {!options.length && <li className="py-6 text-center text-sm text-slate-500" data-testid="person-merge-empty">No one to merge with.</li>}
        </ul>
      </DialogContent>
    </Dialog>
  );
};

// Two different people with the same name: the ticked places move to a person of their own
const SplitPerson = ({ p, open, onOpenChange }) => {
  const qc = useQueryClient();
  const [picked, setPicked] = useState([]);
  const [name, setName] = useState("");
  const close = (o) => { setPicked([]); setName(""); onOpenChange(o); };
  const toggle = (k) => setPicked(picked.includes(k) ? picked.filter((x) => x !== k) : [...picked, k]);
  const save = async () => {
    try {
      await api.splitPerson({ pid: p.key, name, places: p.copies.filter((x) => picked.includes(`${x.w.id}-${x.i}`)).map((x) => ({ wish_id: x.w.id, index: x.i })) });
      toast.success(`${name.trim() || p.name} is now a separate contact`);
      close(false);
      qc.invalidateQueries({ queryKey: ["state"] });
    } catch (e) { err(e, "Couldn't split the contact"); }
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md" data-testid="person-split-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Split {p.name} into two people</DialogTitle>
          <DialogDescription>Tick the places that belong to the other person. The rest stay with this {p.name}.</DialogDescription>
        </DialogHeader>
        <ul className="space-y-1" data-testid="person-split-places">
          {p.copies.map((x, n) => {
            const k = `${x.w.id}-${x.i}`;
            return (
              <li key={k}>
                <label className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-white/5 cursor-pointer">
                  <input type="checkbox" checked={picked.includes(k)} onChange={() => toggle(k)} className="h-4 w-4 accent-emerald-400" data-testid={`person-split-place-${n}`} />
                  <span className="text-sm">{p.places[n]}</span>
                </label>
              </li>
            );
          })}
        </ul>
        <Input placeholder="New name (optional)" value={name} onChange={(e) => setName(e.target.value)} data-testid="person-split-name" />
        <DialogFooter>
          <Button onClick={save} disabled={!picked.length || picked.length >= p.copies.length} className="bg-emerald-400 text-[#04120c] hover:bg-emerald-300" data-testid="person-split-save">Split</Button>
        </DialogFooter>
        {picked.length > 0 && picked.length >= p.copies.length && <p className="text-xs text-rose-300" data-testid="person-split-all-warning">Leave at least one place with this {p.name}.</p>}
      </DialogContent>
    </Dialog>
  );
};

export const PersonActions = ({ p, people, friends, names, testId }) => {
  const qc = useQueryClient();
  const [dlg, setDlg] = useState(null);
  const unlink = async () => {
    try {
      await api.mergePerson({ pid: p.key, friend_id: "" });
      toast.success(`${p.name} unlinked from the profile`);
      qc.invalidateQueries({ queryKey: ["state"] });
    } catch (e) { err(e, "Couldn't unlink"); }
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="p-1.5 text-slate-500 hover:text-slate-200" aria-label={`More for ${p.name}`} data-testid={testId}><MoreVertical size={15} /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setDlg("place")} data-testid={`${testId}-add-place`}><MapPinPlus size={14} /> Add country or city</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => setDlg("merge")} data-testid={`${testId}-merge`}><Merge size={14} /> Merge with…</DropdownMenuItem>
          {p.copies.length > 1 && <DropdownMenuItem onSelect={() => setDlg("split")} data-testid={`${testId}-split`}><Split size={14} /> Split into two people</DropdownMenuItem>}
          {p.friendId && <DropdownMenuItem onSelect={unlink} data-testid={`${testId}-unlink`}><Unlink size={14} /> Unlink from profile</DropdownMenuItem>}
        </DropdownMenuContent>
      </DropdownMenu>
      <AddPlace p={p} names={names} open={dlg === "place"} onOpenChange={(o) => setDlg(o ? "place" : null)} />
      <SplitPerson p={p} open={dlg === "split"} onOpenChange={(o) => setDlg(o ? "split" : null)} />
      <MergeWith p={p} people={people} friends={friends} open={dlg === "merge"} onOpenChange={(o) => setDlg(o ? "merge" : null)} />
    </>
  );
};
